import React, { useState, useRef, useEffect } from 'react';
import {
  MessageCircle,
  Moon,
  Sun,
  User as UserIcon,
  LogOut,
  KeyRound,
  ChevronDown,
  Menu
} from 'lucide-react';
import { User, WhatsAppStatus } from '@/types';
import { resolveApiBase } from '@/utils/apiBase';

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');

interface HeaderProps {
  user: User;
  darkMode: boolean;
  toggleTheme: () => void;
  onLogout: () => void;
  onToggleMobileMenu: () => void;
  pendingCount?: number;
  newMessageTick?: number;
  whatsappStatus?: WhatsAppStatus | null;
  onNavigate?: (page: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ 
  user, 
  darkMode, 
  toggleTheme, 
  onLogout,
  onToggleMobileMenu,
  pendingCount = 0,
  newMessageTick = 0,
  whatsappStatus,
  onNavigate
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isWhatsappReady = whatsappStatus?.status === 'ready';
  const minPasswordLength = 8;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (newMessageTick > 0) {
      setPing(true);
      const t = setTimeout(() => setPing(false), 2000);
      return () => clearTimeout(t);
    }
  }, [newMessageTick]);

  const closeChangePassword = () => {
    setIsChangePasswordOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setChangePasswordError(null);
    setChangePasswordLoading(false);
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setChangePasswordError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setChangePasswordError('Preencha todos os campos.');
      return;
    }
    if (newPassword.length < minPasswordLength) {
      setChangePasswordError(`A nova senha deve ter ao menos ${minPasswordLength} caracteres.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePasswordError('As senhas novas não conferem.');
      return;
    }
    if (newPassword === currentPassword) {
      setChangePasswordError('A nova senha deve ser diferente da senha atual.');
      return;
    }

    setChangePasswordLoading(true);
    try {
      const token = localStorage.getItem('va_token') || '';
      const response = await fetch(`${CORE_API}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          senhaAtual: currentPassword,
          novaSenha: newPassword,
          confirmarSenha: confirmPassword
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao atualizar senha.');
      closeChangePassword();
      alert('Senha atualizada com sucesso.');
    } catch (err: any) {
      setChangePasswordError(err?.message || 'Falha ao atualizar senha.');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  return (
    <>
    <header className={`
      h-16 flex items-center justify-between px-4 md:px-6 border-b transition-colors duration-300 shadow-sm z-20
      ${darkMode ? 'bg-black border-vision-border' : 'bg-white border-gray-200'}
    `}>
      {/* Left side: Mobile Menu & Title */}
      <div className="flex items-center gap-3">
        {/* Mobile Menu Button */}
        <button 
          onClick={onToggleMobileMenu}
          className={`md:hidden p-2 rounded-lg ${darkMode ? 'text-white hover:bg-vision-surface' : 'text-vision-green hover:bg-gray-100'}`}
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Right side: Icons and User Menu */}
      <div className="flex items-center gap-2 md:gap-4">
        
        {/* Chat */}
        <button
          onClick={() => onNavigate?.('atendimento')}
          className={`
          p-2 rounded-full transition-colors relative hidden sm:block
          ${darkMode ? 'text-gray-400 hover:bg-vision-surface' : 'text-vision-green hover:bg-gray-100'}
        `}>
          <MessageCircle size={20} />
          {pendingCount > 0 && (
            <span className={`absolute -top-1 -right-1 min-w-[18px] h-5 px-1 text-[10px] font-bold rounded-full flex items-center justify-center ${isWhatsappReady ? 'bg-green-500 text-white' : 'bg-red-500 text-white'} ${ping ? 'animate-pulse' : ''}`}>
              {pendingCount}
            </span>
          )}
        </button>

        {/* Theme Toggle */}
        <div className={`h-6 w-px mx-1 ${darkMode ? 'bg-vision-border' : 'bg-gray-300'}`}></div>

        <button 
          onClick={toggleTheme}
          className={`
            flex items-center justify-center p-2 rounded-full transition-colors
            ${darkMode ? 'bg-vision-surface text-yellow-400 hover:bg-vision-border' : 'bg-gray-100 text-vision-green hover:bg-gray-200'}
          `}
          title={darkMode ? "Mudar para modo Claro" : "Mudar para modo Escuro"}
        >
          {darkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* User Menu */}
        <div className="relative ml-1 md:ml-2" ref={menuRef}>
          <button 
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className={`
              flex items-center gap-2 pl-2 pr-1 py-1 rounded-full border transition-all
              ${darkMode 
                ? 'border-vision-border bg-vision-surface text-white hover:border-gray-500' 
                : 'border-gray-200 bg-gray-50 text-vision-green hover:border-vision-green'
              }
            `}
          >
            <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center text-gray-500">
               <UserIcon size={20} />
            </div>
            <div className="flex-col items-start hidden md:flex mr-1">
                <span className="text-xs font-bold leading-none">{user.name}</span>
            </div>
            <ChevronDown size={14} className={`transform transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}/>
          </button>

          {/* Dropdown */}
          {isUserMenuOpen && (
            <div className={`
              absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 border origin-top-right z-50
              ${darkMode ? 'bg-vision-darkGreen border-vision-border text-white' : 'bg-white border-gray-200 text-gray-800'}
            `}>
              <div className={`px-4 py-2 border-b mb-1 ${darkMode ? 'border-vision-border' : 'border-gray-200/10'}`}>
                <p className="text-sm font-medium">Minha Conta</p>
                <p className="text-xs opacity-50 md:hidden">{user.name}</p>
              </div>
              <button
                onClick={() => {
                  setIsUserMenuOpen(false);
                  setIsChangePasswordOpen(true);
                }}
                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-50'}`}
              >
                <KeyRound size={16} /> Mudar Senha
              </button>
              <div className={`my-1 border-t ${darkMode ? 'border-vision-border' : 'border-gray-200/10'}`}></div>
              <button 
                onClick={onLogout}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <LogOut size={16} /> Sair
              </button>
            </div>
          )}
        </div>

      </div>
    </header>
    {isChangePasswordOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col ${darkMode ? 'bg-vision-darkGreen border border-vision-border' : 'bg-white'}`}>
          <div className={`p-6 border-b flex items-center justify-between ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
            <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Mudar Senha</h3>
            <button
              onClick={closeChangePassword}
              className={`text-sm font-semibold ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Fechar
            </button>
          </div>
          <form onSubmit={handleChangePassword} className="p-6 space-y-4">
            <div className="space-y-2">
              <label className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Senha atual</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900 text-white' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}`}
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <label className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Nova senha</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900 text-white' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}`}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <label className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Confirmar nova senha</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? 'bg-vision-surface border-vision-border focus:ring-green-900 text-white' : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'}`}
                autoComplete="new-password"
              />
            </div>
            {changePasswordError && (
              <div className={`text-sm font-semibold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                {changePasswordError}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeChangePassword}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:bg-gray-200'}`}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={changePasswordLoading}
                className={`px-6 py-2 rounded-lg font-bold text-white transition-all ${changePasswordLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#294b29] hover:bg-[#1a301a]'}`}
              >
                {changePasswordLoading ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
};

